import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Brain, ShieldCheck, AlertCircle, Download, ArrowLeft, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function VerifyCertificate() {
  const { certificateId } = useParams();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!certificateId) return;
    // ShansoLearn certs start with "shansolearn-"; AI Founder certs use their own format.
    const isShansoLearn = certificateId.toLowerCase().startsWith("shansolearn-");
    const tryEndpoints = isShansoLearn
      ? [`${API}/shanso-learn/verify/${certificateId}`, `${API}/ai-founder/verify/${certificateId}`]
      : [`${API}/ai-founder/verify/${certificateId}`, `${API}/shanso-learn/verify/${certificateId}`];

    (async () => {
      let lastErr = null;
      for (const url of tryEndpoints) {
        try {
          const res = await axios.get(url);
          setCert(res.data);
          setLoading(false);
          return;
        } catch (err) { lastErr = err; }
      }
      setError(lastErr?.response?.data?.detail || "Certificate not found");
      setLoading(false);
    })();
  }, [certificateId]);

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 50% 40%, rgba(0,240,255,0.06) 0%, transparent 60%), radial-gradient(circle at 50% 60%, rgba(112,0,255,0.04) 0%, transparent 60%)" }}
      />

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-link">
            <div className="w-7 h-7 bg-[#00F0FF] rounded-sm flex items-center justify-center">
              <Brain size={15} className="text-black" />
            </div>
            <span className="font-outfit font-bold text-[#00F0FF] text-base">ShansoAI<span className="text-white">Hub</span></span>
          </Link>
          <Link to="/become-ai-founder" className="text-sm text-zinc-400 hover:text-white font-dm-sans transition-colors" data-testid="program-link">
            AI Founder Program
          </Link>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-4 max-w-xl mx-auto">
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
          </div>
        )}

        {error && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-center" data-testid="verify-error">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20">
              <AlertCircle size={36} className="text-red-400" />
            </div>
            <h1 className="font-outfit font-bold text-2xl text-white mb-3">Certificate Not Found</h1>
            <p className="text-zinc-400 font-dm-sans text-sm mb-2">
              The certificate ID <span className="text-zinc-300 font-mono">{certificateId}</span> could not be verified.
            </p>
            <p className="text-zinc-500 font-dm-sans text-xs">
              Please check the ID and try again, or contact ShansoAI Hub for assistance.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 mt-8 text-sm text-[#00F0FF] hover:text-white font-dm-sans transition-colors" data-testid="back-home-btn">
              <ArrowLeft size={15} /> Back to Home
            </Link>
          </motion.div>
        )}

        {cert && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="verify-success">
            {/* Verified badge */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-tr from-[#00F0FF] to-[#7000FF] rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(0,240,255,0.3)]">
                <ShieldCheck size={38} className="text-black" />
              </div>
              <div className="inline-flex items-center gap-2 bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20 px-4 py-1.5 rounded-full text-sm font-dm-sans font-semibold">
                <ShieldCheck size={14} /> Verified Certificate
              </div>
            </div>

            {/* Certificate Card */}
            <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#0F0F15] to-[#050508]">
              {/* Background glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[200px] bg-[radial-gradient(ellipse_at_center,rgba(0,240,255,0.08)_0%,transparent_70%)] pointer-events-none" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[300px] h-[200px] bg-[radial-gradient(ellipse_at_center,rgba(112,0,255,0.06)_0%,transparent_70%)] pointer-events-none" />

              <div className="relative p-8 md:p-10 text-center">
                {/* Org */}
                <p className="text-xs font-dm-sans font-semibold tracking-[0.15em] uppercase text-[#00F0FF] mb-6">
                  ShansoAI Hub
                </p>

                {/* Title */}
                <h1 className="font-outfit font-bold text-2xl md:text-3xl text-white mb-2">
                  Certificate of Completion
                </h1>
                <p className="text-zinc-500 font-dm-sans text-sm mb-8">This is to certify that</p>

                {/* Name */}
                <h2 className="font-outfit font-black text-3xl md:text-4xl mb-2"
                  style={{ background: "linear-gradient(135deg, #00F0FF, #7000FF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  {cert.user_name || "Participant"}
                </h2>

                <p className="text-zinc-500 font-dm-sans text-sm mt-6 mb-2">
                  has successfully completed all levels of the
                </p>
                <p className="font-outfit font-bold text-xl text-white mb-8">
                  {cert.program || "AI Founder Program"}
                </p>

                {/* Divider */}
                <div className="w-24 h-px mx-auto bg-gradient-to-r from-transparent via-[#00F0FF]/40 to-transparent mb-6" />

                {/* Details */}
                <div className="flex items-center justify-center gap-6 text-sm">
                  <div>
                    <p className="text-[10px] font-dm-sans font-semibold tracking-wider uppercase text-zinc-600 mb-1">Issued</p>
                    <p className="font-dm-sans text-zinc-300">{cert.issued_at?.slice(0, 10) || "N/A"}</p>
                  </div>
                  <div className="w-px h-8 bg-zinc-800" />
                  <div>
                    <p className="text-[10px] font-dm-sans font-semibold tracking-wider uppercase text-zinc-600 mb-1">Certificate ID</p>
                    <p className="font-mono text-zinc-300 text-xs">{cert.certificate_id}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Download button — only for AI Founder certs (ShansoLearn certs are downloaded from the learn page with the magic token) */}
            {cert.kind !== "shanso_learn_course" && (
              <div className="text-center mt-6">
                <a
                  href={`${API}/ai-founder/certificate/${cert.certificate_id}/pdf`}
                  data-testid="download-cert-btn"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-black font-outfit font-bold px-6 py-3 rounded-full hover:opacity-90 transition-opacity text-sm"
                >
                  <Download size={16} /> Download PDF
                </a>
              </div>
            )}

            <p className="text-center text-zinc-600 text-xs font-dm-sans mt-8">
              This certificate has been verified by ShansoAI Hub.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
